import { useState, useEffect } from 'react';
import './index.css';
import Login           from './Login';
import TimeTracker     from './TimeTracker';
import Report          from './Report';
import Projects        from './Projects';
import Invoices        from './Invoices';
import UpworkDashboard from './UpworkDashboard';
import UpworkSetup     from './UpworkSetup';
import { api } from './api';

export default function App() {
  const [booting,   setBooting]   = useState(true);
  const [authed,    setAuthed]    = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [bypass,    setBypass]    = useState(false);
  const [demo,      setDemo]      = useState(false);
  const [logoutUrl, setLogoutUrl] = useState('/.auth/logout?post_logout_redirect_uri=/');
  // Land on the Setup tab when returning from the Upwork OAuth callback
  // (/?upwork_connected=1) so the user sees the success banner.
  const [tab,       setTab]       = useState(
    new URLSearchParams(window.location.search).get('upwork_connected') === '1' ? 'setup' : 'tracker');
  const [projects,  setProjects]  = useState([]);

  // On mount: ask server who we are. 200 → authed, 401 → show login.
  // Three response shapes the server can send:
  //   { email, bypass:true  }            → local dev with AUTH_DEV_BYPASS
  //   { email, demo:true    }            → public marketing demo (uptracker-demo)
  //   { email, logout_url, ... }         → real BPS handoff session
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        if (res.ok) {
          const info = await res.json();
          setUserEmail(info.email);
          setBypass(!!info.bypass);
          setDemo(!!info.demo);
          if (info.logout_url) setLogoutUrl(info.logout_url);
          setAuthed(true);
        }
      } catch {
        /* server unreachable — treat as unauth */
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  async function loadProjects() {
    try {
      setProjects(await api.getProjects());
    } catch {
      /* 401 → api.js redirects through /AuthBridge/Login (BPS sign-in) */
    }
  }

  useEffect(() => { if (authed) loadProjects(); }, [authed]);

  function handleSignOut() {
    // In dev-bypass there's no real session to clear. Reload is a no-op
    // visually; we just don't offer a sign-out button in that mode (below).
    window.location.href = logoutUrl;
  }

  // Boot: still figuring out auth state
  if (booting) {
    return (
      <div className="login-wrap">
        <div className="login-card"><p style={{ color: '#737370' }}>Loading…</p></div>
      </div>
    );
  }

  if (!authed) return <Login />;

  return (
    <div className="app-shell">
      {bypass && (
        <div style={{
          background: '#FEF3C7',
          borderBottom: '1px solid #F59E0B',
          color: '#92400E',
          fontSize: 12,
          padding: '6px 12px',
          textAlign: 'center',
          fontFamily: 'monospace',
        }}>
          ⚠ AUTH_DEV_BYPASS active — every request runs as {userEmail}. Not authenticated.
        </div>
      )}

      {demo && (
        <div style={{
          background: '#FFEDD5',
          borderBottom: '1px solid #F97316',
          color: '#9A3412',
          fontSize: 13,
          padding: '8px 12px',
          textAlign: 'center',
        }}>
          <strong>Demo mode</strong> — sample data only. Try any feature freely; the database resets nightly.
          {' '}<a href="https://bps-corp.com/Products" style={{ color: '#9A3412', textDecoration: 'underline' }}>Back to products</a>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <h1 className="app-title">Time Tracker</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {userEmail && (
            <span style={{ fontSize: 12, color: '#737370' }}>{userEmail}</span>
          )}
          {!bypass && !demo && (
            <button
              className="btn-outline"
              style={{ fontSize: 13 }}
              onClick={handleSignOut}
            >
              Sign out
            </button>
          )}
        </div>
      </div>

      <div className="tabs">
        <button className={`tab${tab === 'tracker'  ? ' active' : ''}`} onClick={() => setTab('tracker')}>Time Tracker</button>
        <button className={`tab${tab === 'report'   ? ' active' : ''}`} onClick={() => setTab('report')}>Report</button>
        <button className={`tab${tab === 'projects' ? ' active' : ''}`} onClick={() => setTab('projects')}>Projects</button>
        <button className={`tab${tab === 'invoices' ? ' active' : ''}`} onClick={() => setTab('invoices')}>Invoices</button>
        <button className={`tab${tab === 'upwork'   ? ' active' : ''}`} onClick={() => setTab('upwork')}>Upwork</button>
        <button className={`tab${tab === 'setup'    ? ' active' : ''}`} onClick={() => setTab('setup')}>Setup</button>
        <a className="tab" href="https://bps-corp.com/Guides/up-tracker" target="_blank" rel="noopener" title="How to use Up-Tracker — opens the user guide">Docs</a>
      </div>

      {tab === 'tracker'  && <TimeTracker projects={projects} onProjectsChange={loadProjects} />}
      {tab === 'report'   && <Report      projects={projects} />}
      {tab === 'projects' && <Projects    projects={projects} onProjectsChange={loadProjects} />}
      {tab === 'invoices' && <Invoices    projects={projects} />}
      {tab === 'upwork'   && <UpworkDashboard />}
      {tab === 'setup'    && <UpworkSetup />}
    </div>
  );
}
